import { useEffect, useState } from 'react';
import { useNavigate } from '@usas/ui-web/wrappers/ReactRouterDom';

import NavLinks from '../../NavLinks';

import validate from '../../components/forms/GroupRegistrationFormValidation';

import { createGroupObjFromGroupRegistrationForm } from '../../utils/GroupUtils';

import useGroupAddData from '../../../../state/groupAdd/UseGroupAddData';

import useEnvironmentVariableData from '@usas/ui-web/state/environmentVariable/UseEnvironmentVariableData';
import useOrgRoleStaffRoleHierarchyData from '@usas/ui-web/state/orgRoleStaffRoleHierarchy/UseOrgRoleStaffRoleHierarchyData';
import useOrgGroupTypesData from '@usas/ui-web/state/orgGroupTypes/UseOrgGroupTypesData';
import useGroupFieldNamesData from '@usas/ui-web/state/groupFieldNames/UseGroupFieldNamesData';

import useForm from '@usas/ui-web/utils/UseForm';
import Constants from '@usas/ui-web/utils/Constants';
import HierarchicalDataUtils from '@usas/ui-web/utils/HierarchicalDataUtils';
import { formatDate } from '@usas/ui-web/utils/DateFunctions';

const useGroupRegistrationAdd = () => {
  const navigate = useNavigate();
  const { environmentVariableState, GROUP_ROLES_ROLE_GROUP_ID } = useEnvironmentVariableData();
  const { GROUP_CONSTANTS } = useGroupFieldNamesData();
  const { orgGroupTypesState } = useOrgGroupTypesData();
  const { orgRoleStaffRoleHierarchyState } = useOrgRoleStaffRoleHierarchyData();
  const { groupAddState, updateGroupAddObj, postGroup } = useGroupAddData();
  const { formState, errorState, onFormValueChanged, handleSubmit, setFormState, setIsDirty
  } = useForm(getInitialFormState, submitFormCallback, validate);
  const [state, setState] = useState(getInitialState);

  const onBackClicked = () => {
    updateGroupAddObj(createGroupObjFromGroupRegistrationForm(groupAddState.objData, formState));

    setState({ ...state, tryRedirect: true });
  };

  useEffect(() => {
    if (groupAddState.isObjLoaded === true) {
      if (orgRoleStaffRoleHierarchyState.isArrayLoaded === true && orgGroupTypesState.isArrayLoaded === true
        && environmentVariableState.isLoaded === true && orgRoleStaffRoleHierarchyState.roleGroupId === GROUP_ROLES_ROLE_GROUP_ID) {
        const groupObj = groupAddState.objData;

        setFormState({
          ...formState,
          hasRegistration: groupObj.hasRegistration ?? '',
          requiresApproval: groupObj.requiresApproval ?? '',
          hasOpenRegistration: groupObj.hasOpenRegistration ?? '',
          hasRequiresApproval: getHasRequiresApproval(groupObj.groupTypeId, orgGroupTypesState.arrayData),
          hasOrgRoleRestriction: groupObj.hasOrgRoleRestriction ?? '',
          registrationCloseDate: groupObj.registrationCloseDate ? formatDate(groupObj.registrationCloseDate) : Constants.BLANK_DATE_STRING,
          registrationOpenDate: groupObj.registrationOpenDate ? formatDate(groupObj.registrationOpenDate) : Constants.BLANK_DATE_STRING,
          eligibleRoles: HierarchicalDataUtils.GetNameIdPairs(orgRoleStaffRoleHierarchyState.treeData, groupObj.orgGroupEligibility.map(x => x.orgRoleId))
        });

        setIsDirty(true);
      }
    } else {
      navigate(NavLinks.GROUP_ADD);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgRoleStaffRoleHierarchyState, orgGroupTypesState]);

  useEffect(() => {
    if (state.tryRedirect === true) { //After back button clicked
      const targetGroupTypeName = groupAddState.groupTypeName;

      switch (targetGroupTypeName) {
        case GROUP_CONSTANTS.GROUP_TYPE_NAME_LIST:
          navigate(NavLinks.GROUP_ADD_LIST);
          break;
        case GROUP_CONSTANTS.GROUP_TYPE_NAME_COMPETITOR_LIST:
          navigate(NavLinks.GROUP_ADD_COMPETITOR_LIST);
          break;
        case GROUP_CONSTANTS.GROUP_TYPE_NAME_CAMP:
          navigate(NavLinks.GROUP_ADD_CAMP);
          break;
        case GROUP_CONSTANTS.GROUP_TYPE_NAME_SAA:
          navigate(NavLinks.GROUP_ADD_SCHOLASTIC);
          break;
        case GROUP_CONSTANTS.GROUP_TYPE_NAME_NATIONAL_TEAM:
          navigate(NavLinks.GROUP_ADD_NATIONALTEAM);
          break;
        default:
          navigate(NavLinks.GROUP_ADD);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.tryRedirect]);

  function getHasRequiresApproval(groupTypeId, groupTypes) {
    const currentGroupType = groupTypes.find(x => x.groupTypeId === groupTypeId);

    if (currentGroupType) {
      return currentGroupType.hasRequiresApprovalOption === true;
    } else {
      return false;
    }
  }

  async function submitFormCallback(formState) {
    const groupObj = JSON.parse(JSON.stringify(groupAddState.objData));

    const postGroupResult = await postGroup(createGroupObjFromGroupRegistrationForm(groupObj, formState));

    if (postGroupResult) {
      const orgGroupId = groupAddState.objData?.orgGroupId;

      navigate(NavLinks.GROUP_ADD_MEMBERS, { state: { orgGroupId } });
    }
  }

  function getInitialFormState() {
    return {
      hasRegistration: '',
      requiresApproval: '',
      hasOpenRegistration: '',
      hasRequiresApproval: false,
      hasOrgRoleRestriction: '',
      registrationCloseDate: Constants.BLANK_DATE_STRING,
      registrationOpenDate: Constants.BLANK_DATE_STRING,
      eligibleRoles: []
    };
  }

  function getInitialState() {
    return {
      tryRedirect: false
    };
  }

  return {
    isSaving: groupAddState.isObjLoading,
    isLoading: environmentVariableState.isLoading || orgGroupTypesState.isArrayLoading,
    formState,
    errorState,
    handleSubmit,
    onFormValueChanged,
    onBackClicked,
    groupTypeName: groupAddState.groupTypeName,
    GROUP_ROLES_ROLE_GROUP_ID: GROUP_ROLES_ROLE_GROUP_ID || Constants.DEFAULT_ID
  };
};

export default useGroupRegistrationAdd;